import { useEffect, useState } from "react";
import { ListFilter, Plus, Trash2, Pencil, Users, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListMembersDialog } from "@/components/lists/ListMembersDialog";
import { CustomCriteriaEditor, customCriteriaError } from "@/components/lists/CustomCriteriaEditor";
import { ImportWizardL3 } from "@/components/import/ImportWizardL3";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#EC4899"];
const EMPTY = { status: "any", cluster_ids: [], has_email: "any", tags: [], created_within_days: "any", phone_prefix: "", store: "any", city: "any", clicked_within_days: "any", sms_reach: false, custom: [], custom_match: "all", form_completed_id: "any", form_not_completed_id: "any" };

function toApi(c) {
  return {
    status: c.status === "any" ? null : c.status,
    cluster_ids: c.cluster_ids,
    has_email: c.has_email === "any" ? null : c.has_email === "yes",
    tags: c.tags,
    created_within_days: c.created_within_days === "any" ? null : parseInt(c.created_within_days, 10),
    phone_prefix: c.phone_prefix || null,
    store: c.store === "any" ? null : c.store,
    city: c.city === "any" ? null : c.city,
    clicked_within_days: c.clicked_within_days === "any" ? null : parseInt(c.clicked_within_days, 10),
    clicked_campaign_id: c.clicked_campaign_id || null,
    not_clicked_campaign_id: c.not_clicked_campaign_id || null,
    min_clicks_90d: c.min_clicks_90d || null,
    sms_reach: c.sms_reach ? true : null,
    undelivered_campaign_id: c.undelivered_campaign_id || null,
    tapped_button: c.tapped_button || null,
    form_completed_id: c.form_completed_id === "any" ? null : c.form_completed_id || null,
    form_not_completed_id: c.form_not_completed_id === "any" ? null : c.form_not_completed_id || null,
    pause_ends_within_days: c.pause_filter === "ends7" ? 7 : c.pause_filter === "ends30" ? 30 : null,
    reactivated_within_days: c.pause_filter === "react7" ? 7 : c.pause_filter === "react30" ? 30 : null,
    custom: (c.custom || []).filter((x) => x.field && x.op).map((x) => ({ field: x.field, op: x.op, value: x.value || null, value2: x.value2 || null, values: x.values || [] })),
    custom_match: c.custom_match || "all",
  };
}
function fromApi(c = {}) {
  return {
    status: c.status || "any",
    cluster_ids: c.cluster_ids || [],
    has_email: c.has_email === true ? "yes" : c.has_email === false ? "no" : "any",
    tags: c.tags || [],
    created_within_days: c.created_within_days ? String(c.created_within_days) : "any",
    phone_prefix: c.phone_prefix || "",
    store: c.store || "any",
    city: c.city || "any",
    clicked_within_days: c.clicked_within_days ? String(c.clicked_within_days) : "any",
    clicked_campaign_id: c.clicked_campaign_id || null,
    not_clicked_campaign_id: c.not_clicked_campaign_id || null,
    min_clicks_90d: c.min_clicks_90d || null,
    sms_reach: !!c.sms_reach,
    form_completed_id: c.form_completed_id || "any",
    form_not_completed_id: c.form_not_completed_id || "any",
    undelivered_campaign_id: c.undelivered_campaign_id || null,
    tapped_button: c.tapped_button || null,
    pause_filter: c.pause_ends_within_days === 7 ? "ends7" : c.pause_ends_within_days === 30 ? "ends30" : c.reactivated_within_days === 7 ? "react7" : c.reactivated_within_days === 30 ? "react30" : "any",
    custom: (c.custom || []).map((x) => ({ field: x.field, op: x.op, value: x.value || "", value2: x.value2 || "", values: x.values || [] })),
    custom_match: c.custom_match || "all",
  };
}

export default function Lists() {
  const [lists, setLists] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [tags, setTags] = useState([]);
  const [stores, setStores] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewList, setViewList] = useState(null);
  const [membersList, setMembersList] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    const [l, cl, tg, st, ci] = await Promise.all([
      api.get("/lists"), api.get("/clusters"), api.get("/contacts/tags"),
      api.get("/contacts/stores"), api.get("/contacts/cities"),
    ]);
    setLists(l.data);
    setClusters(cl.data);
    setTags(tg.data);
    setStores(st.data);
    setCities(ci.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/lists/${id}`);
    toast.success("Lista eliminata");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-100">Liste dinamiche</h1>
          <p className="text-slate-400 text-sm mt-1">Crea segmenti di clienti in base a criteri: si aggiornano automaticamente</p>
        </div>
        <ListDialog clusters={clusters} tags={tags} stores={stores} cities={cities} onDone={load} trigger={
          <Button data-testid="create-list-button" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            <Plus className="h-4 w-4 mr-2" /> Nuova lista
          </Button>
        } />
      </div>

      {loading ? (
        <p className="text-slate-500 text-center py-10">Caricamento...</p>
      ) : lists.length === 0 ? (
        <div className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-10 text-center">
          <ListFilter className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessuna lista ancora. Crea la tua prima lista per criteri.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lists-grid">
          {lists.map((l) => (
            <div key={l.id} data-testid={`list-card-${l.id}`}
                 className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 hover:border-[#10B981]/40 transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${l.color}22` }}>
                    <ListFilter className="h-5 w-5" style={{ color: l.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-slate-100 font-medium truncate">{l.name}</p>
                    <p className="text-xs text-slate-500">{l.contact_count} contatti · <span data-testid={`list-kind-${l.id}`}>{l.kind === "static" ? "statica" : "dinamica"}</span>{(l.member_ids?.length > 0 || l.excluded_ids?.length > 0) && <> · {l.kind === "static" ? "" : `${l.member_ids?.length || 0} manuali, `}{l.excluded_ids?.length || 0} esclusi</>}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <ListDialog clusters={clusters} tags={tags} stores={stores} cities={cities} existing={l} onDone={load} trigger={
                    <button data-testid={`edit-list-${l.id}`} className="text-slate-500 hover:text-[#3B82F6] p-1"><Pencil className="h-4 w-4" /></button>
                  } />
                  <button onClick={() => remove(l.id)} data-testid={`delete-list-${l.id}`} className="text-slate-500 hover:text-[#EF4444] p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {l.description && <p className="text-sm text-slate-400 mt-3 line-clamp-2">{l.description}</p>}
              <Button variant="outline" data-testid={`view-list-${l.id}`} onClick={() => setViewList(l)}
                      className="mt-4 w-full bg-[#1E293B] border-[#1F2937] text-slate-200 hover:bg-[#334155]">
                <Eye className="h-4 w-4 mr-2" /> Vedi contatti
              </Button>
              <Button variant="outline" data-testid={`members-list-${l.id}`} onClick={() => setMembersList(l)}
                      className="mt-2 w-full bg-[#1E293B] border-[#1F2937] text-slate-200 hover:bg-[#334155]">
                <Users className="h-4 w-4 mr-2" /> Gestisci membri
              </Button>
              <div className="mt-2 [&>button]:w-full" data-testid={`update-from-file-${l.id}`}><ImportWizardL3 clusters={clusters} onDone={load} defaultList={l} /></div>
            </div>
          ))}
        </div>
      )}

      <ViewContactsDialog list={viewList} onClose={() => setViewList(null)} />
      <ListMembersDialog list={membersList} onClose={() => setMembersList(null)} onChanged={load} isAdmin={user?.role === "admin"} />
    </div>
  );
}

function ListDialog({ clusters, tags, stores = [], cities = [], existing, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#3B82F6");
  const [crit, setCrit] = useState(EMPTY);
  const [count, setCount] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(existing?.name || "");
      setDescription(existing?.description || "");
      setColor(existing?.color || "#3B82F6");
      setCrit(existing ? fromApi(existing.criteria) : EMPTY);
    }
  }, [open, existing]);

  useEffect(() => {
    if (!open) return;
    if (customCriteriaError(crit.custom)) { setCount(null); return; }
    const t = setTimeout(() => {
      api.post("/lists/preview", { name: name || "x", criteria: toApi(crit) })
        .then((r) => setCount(r.data.count)).catch(() => setCount(null));
    }, 250);
    return () => clearTimeout(t);
  }, [crit, open, name]);

  const toggle = (key, id) =>
    setCrit((c) => ({ ...c, [key]: c[key].includes(id) ? c[key].filter((x) => x !== id) : [...c[key], id] }));
  const critError = customCriteriaError(crit.custom);

  const save = async () => {
    if (!name.trim()) { toast.error("Inserisci un nome per la lista"); return; }
    if (critError) { toast.error(`Criterio su campo personalizzato incompleto: ${critError}`); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description, color, criteria: toApi(crit), ...(existing ? { kind: existing.kind || "dynamic", member_ids: existing.member_ids || [], excluded_ids: existing.excluded_ids || [] } : {}) };
      if (existing) await api.put(`/lists/${existing.id}`, payload);
      else await api.post("/lists", payload);
      toast.success(existing ? "Lista aggiornata" : "Lista creata");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Modifica lista" : "Nuova lista"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nome lista</Label>
            <Input data-testid="list-name-input" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Es. Iscritti recenti con email" className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <div className="space-y-2"><Label>Descrizione (opzionale)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)}
                   className="bg-[#0A0E17] border-[#1F2937]" /></div>
          <div className="space-y-2"><Label>Colore</Label>
            <div className="flex gap-2">
              {COLORS.map((col) => (
                <button key={col} type="button" onClick={() => setColor(col)}
                        className={`h-7 w-7 rounded-full transition-transform ${color === col ? "ring-2 ring-white scale-110" : ""}`}
                        style={{ backgroundColor: col }} />
              ))}
            </div>
          </div>

          <div className="border-t border-[#1F2937] pt-4 space-y-4">
            <p className="text-sm font-medium text-slate-300">Criteri (combinati insieme)</p>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Stato consenso</Label>
                <Select value={crit.status} onValueChange={(v) => setCrit({ ...crit, status: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="crit-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="any">Qualsiasi</SelectItem>
                    <SelectItem value="subscribed">Iscritto</SelectItem>
                    <SelectItem value="unsubscribed">Disiscritto</SelectItem>
                    <SelectItem value="paused">In pausa (STOP temporaneo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Pausa / riattivazione</Label>
                <Select value={crit.pause_filter || "any"} onValueChange={(v) => setCrit({ ...crit, pause_filter: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="crit-pause"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="any">Qualsiasi</SelectItem>
                    <SelectItem value="ends7">Pausa in scadenza entro 7 giorni</SelectItem>
                    <SelectItem value="ends30">Pausa in scadenza entro 30 giorni</SelectItem>
                    <SelectItem value="react7">Riattivati da fine pausa negli ultimi 7 giorni</SelectItem>
                    <SelectItem value="react30">Riattivati da fine pausa negli ultimi 30 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Email</Label>
                <Select value={crit.has_email} onValueChange={(v) => setCrit({ ...crit, has_email: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="crit-email"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="any">Qualsiasi</SelectItem>
                    <SelectItem value="yes">Con email</SelectItem>
                    <SelectItem value="no">Senza email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Iscritto negli ultimi</Label>
                <Select value={crit.created_within_days} onValueChange={(v) => setCrit({ ...crit, created_within_days: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="crit-days"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="any">Qualsiasi periodo</SelectItem>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                    <SelectItem value="90">90 giorni</SelectItem>
                    <SelectItem value="365">1 anno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormCriteria crit={crit} setCrit={setCrit} />
              <div className="space-y-1.5"><Label className="text-xs">Ha cliccato un link negli ultimi</Label>
                <Select value={crit.clicked_within_days} onValueChange={(v) => setCrit({ ...crit, clicked_within_days: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="crit-clicked-days"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="any">Non filtrare</SelectItem>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                    <SelectItem value="90">90 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Prefisso telefono</Label>
                <Input data-testid="crit-prefix" value={crit.phone_prefix} onChange={(e) => setCrit({ ...crit, phone_prefix: e.target.value })}
                       placeholder="+39" className="bg-[#0A0E17] border-[#1F2937] h-9" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Punto vendita</Label>
                <Select value={crit.store} onValueChange={(v) => setCrit({ ...crit, store: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="crit-store"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="any">Qualsiasi</SelectItem>
                    {stores.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Città</Label>
                <Select value={crit.city} onValueChange={(v) => setCrit({ ...crit, city: v })}>
                  <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid="crit-city"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
                    <SelectItem value="any">Qualsiasi</SelectItem>
                    {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5"><Label className="text-xs">Appartiene a cluster</Label>
              <div className="flex flex-wrap gap-2">
                {clusters.length === 0 && <span className="text-xs text-slate-500">Nessun cluster</span>}
                {clusters.map((c) => (
                  <button key={c.id} type="button" onClick={() => toggle("cluster_ids", c.id)}
                          data-testid={`crit-cluster-${c.id}`}
                          className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                            crit.cluster_ids.includes(c.id) ? "bg-[#10B981] text-[#0A0E17] border-[#10B981]" : "border-[#1F2937] text-slate-400 hover:border-[#10B981]/40"}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5"><Label className="text-xs">Con tag</Label>
              <div className="flex flex-wrap gap-2">
                {tags.length === 0 && <span className="text-xs text-slate-500">Nessun tag disponibile</span>}
                {tags.map((t) => (
                  <button key={t} type="button" onClick={() => toggle("tags", t)}
                          data-testid={`crit-tag-${t}`}
                          className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                            crit.tags.includes(t) ? "bg-[#8B5CF6] text-white border-[#8B5CF6]" : "border-[#1F2937] text-slate-400 hover:border-[#8B5CF6]/40"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <button type="button" onClick={() => setCrit({ ...crit, sms_reach: !crit.sms_reach })} data-testid="crit-sms-reach"
                    className={`w-full text-left rounded-xl border px-3 py-2 transition-all ${crit.sms_reach ? "bg-[#3B82F6]/15 border-[#3B82F6]/50" : "border-[#1F2937] hover:border-[#3B82F6]/40"}`}>
              <p className={`text-sm ${crit.sms_reach ? "text-[#93C5FD]" : "text-slate-300"}`}>Da raggiungere via SMS</p>
              <p className="text-xs text-slate-500">Chi preferisce l'SMS oppure è segnato «Non raggiungibile» su WhatsApp</p>
            </button>
            <CustomCriteriaEditor items={crit.custom || []} match={crit.custom_match || "all"} onChange={(custom) => setCrit({ ...crit, custom })} onMatch={(custom_match) => setCrit({ ...crit, custom_match })} />
          </div>

          <div className="bg-[#0A0E17] border border-[#1F2937] rounded-xl px-4 py-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-[#10B981]" />
            <span className="text-sm text-slate-300">Contatti corrispondenti:</span>
            <span className="text-sm font-semibold text-[#10B981]" data-testid="list-preview-count">
              {count === null ? "…" : count}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !!critError} data-testid="save-list-button"
                  className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (existing ? "Salva modifiche" : "Crea lista")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewContactsDialog({ list, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (list) {
      setLoading(true);
      api.get(`/lists/${list.id}/contacts`).then((r) => setContacts(r.data)).catch(() => setContacts([])).finally(() => setLoading(false));
    }
  }, [list]);

  return (
    <Dialog open={!!list} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{list?.name} — {contacts.length} contatti</DialogTitle></DialogHeader>
        {loading ? (
          <p className="text-slate-500 text-center py-8">Caricamento...</p>
        ) : contacts.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Nessun contatto corrisponde a questi criteri.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="list-contacts-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#1F2937] text-slate-500 text-left">
                <th className="px-3 py-2 font-medium">Nome</th>
                <th className="px-3 py-2 font-medium">Telefono</th>
                <th className="px-3 py-2 font-medium">Punto vendita</th>
                <th className="px-3 py-2 font-medium">Città</th>
                <th className="px-3 py-2 font-medium">Stato</th>
              </tr></thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-[#1F2937]/60">
                    <td className="px-3 py-2 text-slate-200">{c.name}</td>
                    <td className="px-3 py-2 font-mono2 text-slate-300">{c.phone}</td>
                    <td className="px-3 py-2 text-slate-300">{c.store || <span className="text-slate-600">—</span>}</td>
                    <td className="px-3 py-2 text-slate-300">{c.city || <span className="text-slate-600">—</span>}</td>
                    <td className="px-3 py-2">
                      {c.status === "subscribed"
                        ? <Badge className="bg-[#10B981]/15 text-[#10B981] border-0">Iscritto</Badge>
                        : <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-0">Disiscritto</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormCriteria({ crit, setCrit }) {
  const [forms, setForms] = useState(null);
  useEffect(() => { api.get("/forms").then((r) => setForms(r.data.items || r.data || [])).catch(() => setForms([])); }, []);
  if (!forms || !forms.length) return null;
  const sel = (key, label, testid) => (
    <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
      <Select value={crit[key] || "any"} onValueChange={(v) => setCrit({ ...crit, [key]: v })}>
        <SelectTrigger className="bg-[#0A0E17] border-[#1F2937] h-9" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#111827] border-[#1F2937] text-slate-100">
          <SelectItem value="any">Non filtrare</SelectItem>
          {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>);
  return <>{sel("form_completed_id", "Ha completato il Form", "crit-form-completed")}{sel("form_not_completed_id", "Invitato al Form senza risposta", "crit-form-not-completed")}</>;
}
